import { useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Badge, Box, Button, Heading, HStack, IconButton, Text, Tooltip, useToast } from "@chakra-ui/react";
import { HiOutlineArrowNarrowLeft } from "react-icons/hi";
import Navbar from "../Navbar";
import { documentExtensions } from "./documentExtensions";
import { getStoredToken } from "../utils/installAuthToken";
import { errorFromResponse } from "../utils/apiErrors";
import { showApiErrorToast } from "../utils/showApiToast";

const DOCS_API = "https://documents.poc.mrc.ai/api/documents";

/**
 * A contract document opened by someone who may read but not change it, e.g. a
 * carrier opening the document on a submission sent to them. Renders the saved
 * content with the editor's own extensions; no toolbar, no editing, and no
 * live collaboration session (that's for the owning broker only).
 */
const DocumentReadOnlyView = ({ documentId, name, content, onBack }) => {
  const toast = useToast();
  const [exporting, setExporting] = useState(false);

  const editor = useEditor(
    {
      extensions: documentExtensions(),
      content,
      editable: false,
      shouldRerenderOnTransaction: false,
    },
    [documentId]
  );

  const exportPdf = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${DOCS_API}/${documentId}/render/pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getStoredToken()}` },
      });
      if (!response.ok) throw await errorFromResponse(response, `Failed to generate PDF (HTTP ${response.status})`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name || "document"}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't export PDF" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box minHeight="100vh" bg="gray.50">
      <Navbar />
      <Box p={4} maxW="1200px" mx="auto">
        <HStack justify="space-between" borderWidth="1px" borderRadius="md" bg="white" p={3} mb={3}>
          <HStack spacing={3}>
            <Tooltip label="Back">
              <IconButton icon={<HiOutlineArrowNarrowLeft />} size="sm" aria-label="Back" onClick={onBack} />
            </Tooltip>
            <Heading size="md">{name || "Document"}</Heading>
            <Badge colorScheme="gray">Read-only</Badge>
          </HStack>
          <HStack>
            <Text fontSize="xs" color="gray.500">Shared with you on a submission</Text>
            <Button size="sm" onClick={exportPdf} isLoading={exporting}>Export PDF</Button>
          </HStack>
        </HStack>
        <Box borderWidth="1px" borderRadius="md" bg="whiteAlpha.900" overflow="auto">
          <div className="editor-wrapper">
            <div className="editor-page">
              <EditorContent editor={editor} />
            </div>
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentReadOnlyView;
